import React, { useEffect, useRef, useState } from 'react';
import { loadModules } from 'esri-loader';
import './styles.css';
import * as utils from '../../utils';
import * as api from '../../api';
import Loader from '../../Loader';

const requestImageFile = require.context('../../images', true);

// Función encargada de inicializar buscador de fuente customizada
export const createCustomSearch = ({ SearchSource, geometryEngine, Graphic }) => {
  return new SearchSource({
    placeholder: 'Buscar dirección o lugar',
    getSuggestions: async params => {
      const limit = 10;
      const { data: results = [] } = await api.search({ limit, q: params.suggestTerm });
      return results.slice(0, limit + 20).map(d => {
        return {
          key: 'id',
          text: d.nombre,
          sourceIndex: params.sourceIndex,
        };
      });
    },
    getResults: async params => {
      const { data: results = [] } = await api.search({ limit: 5, q: params.suggestResult.text });
      const searchResults = results.map(d => {
        const graphic = d.categoria === 'Parcela' ? utils.createPolygonGraphic(Graphic, d) : utils.createGraphic(Graphic, d);
        const buffer = geometryEngine.geodesicBuffer(
          graphic.geometry,
          100,
          'meters',
        );
        const searchResult = {
          extent: buffer.extent,
          feature: graphic,
          name: d.nombre,
        };
        return searchResult;
      });

      return searchResults;
    },
  });
};

const WebMapView = () => {
  const mapRef = useRef();
  const [esriPackages, setEsriPackages] = useState(null);
  const [capas, setCapas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getEsriInitialData = async () => {
    setIsLoading(true);
    try {
      const results = await loadModules([
        'esri/Map',
        'esri/views/MapView',
        'esri/widgets/Search',
        'esri/widgets/BasemapGallery',
        'esri/widgets/Expand',
        'esri/widgets/LayerList',
        'esri/widgets/Zoom',
        'esri/widgets/Locate',
        'esri/layers/FeatureLayer',
        'esri/Graphic',
        'esri/widgets/Search/SearchSource',
        'esri/geometry/geometryEngine',
        'dojo/domReady!',
      ], {
        css: true,
        dojoConfig: {
          locale: 'es',
          parseOnLoad: true,
        },
      });
      const { data = {} } = await api.getInitialData();
      const { capas: capasIniciales = [] } = data;
      setCapas(capasIniciales);
      setEsriPackages(results);
      setIsLoading(false);

    } catch (e) {
      setIsLoading(false);
      // console.log('Ha ocurrido un error por favor intente nuevamente');
      // console.log(e);
    }
  };

  const getInitialLayers = (FeatureLayer, Graphic) => {
    const initialLayers = capas.map(c => {
      let icon;
      if (c.icono) {
        try {
          icon = requestImageFile(`./${c.icono}`).default;
        } catch {
          icon = requestImageFile('./i_pin2.png').default;
        }
      } else {
        icon = requestImageFile('./i_pin2.png').default;
      }
      return utils.createLayer({ FeatureLayer, Graphic, data: [], title: c.capa, popupTitle: '{nombre}', icon, capaId: c.id });
    }).reverse();

    const parcelasLayers = utils.createPolygonLayer({ FeatureLayer, Graphic, data: [], title: 'Parcelas', popupTitle: '{NB_INMUEBLE}', capaId: 'parcelas' });
    initialLayers.push(parcelasLayers);

    return initialLayers;

  };

  useEffect(() => {
    if (esriPackages) {
      const [
        ArcGISMap,
        MapView,
        Search,
        BasemapGallery,
        Expand,
        LayerList,
        Zoom,
        Locate,
        FeatureLayer,
        Graphic,
        SearchSource,
        geometryEngine,
      ] = esriPackages;

      const initialLayers = getInitialLayers(FeatureLayer, Graphic);

      const map = new ArcGISMap({
        basemap: 'streets-vector',
        layers: initialLayers,
      });

      // Variable que carga el MapView en la referencia del nodo del DOM
      const view = new MapView({
        container: mapRef.current,
        map,
        center: [-66.85367, 10.49581],
        zoom: 15,
      });

      // Se eliminan todos los componentes de UI por default de MapView
      view.ui.components = [];

      // Se carga a la vista del mapa dos widgets para manejar las capas y las bases de mapa
      const uniqueParentItems = [];
      const defineActions = event => {
        const { item } = event;
        if (!item.parent) {
          if (!uniqueParentItems.includes(item.title)) {
            uniqueParentItems.push(item.title);
            item.watch('visible', async isVisible => {
              try {
                let edits = {};
                const capaId = item.layer?.capaId;
                const currentLayer = capaId ? initialLayers.find(l => l.capaId === capaId) : null;
                if (isVisible) {
                  setIsLoading(true);
                  // console.log('Show feature: ', item.title);
                  // Se obtienen puntos actuales de la capa seleccionada
                  const currentFeatures = await currentLayer.queryFeatures();
                  // Se obtienen los nuevos puntos para la capa seleccionado
                  const { data = [] } = await api.getLayers(capaId);
                  let newGraphicPoints;
                  // Se obtienen los nuevos puntos para la capa seleccionado
                  if (capaId === 'parcelas') {
                    newGraphicPoints = data.map(d => utils.createPolygonGraphic(Graphic, d)).filter(d => d !== null);

                  } else {
                    newGraphicPoints = data.map(d => utils.createGraphic(Graphic, d));

                  }
                  edits = {
                    addFeatures: newGraphicPoints,
                    deleteFeatures: currentFeatures.features,
                  };
                } else {
                // console.log('Hide feature: ', item.title);
                // Se obtienen puntos actuales en el mapa
                  const currentFeatures = await currentLayer.queryFeatures();
                  edits = {
                    deleteFeatures: currentFeatures.features,
                  };
                }
                await currentLayer.applyEdits(edits, {});
                setIsLoading(false);
              } catch {
                setIsLoading(false);
              }
            });
          }
        }
      };

      const layerList = new LayerList({ view, listItemCreatedFunction: defineActions });
      view.ui.add(
        [
          new Expand({
            content: layerList,
            view,
            group: 'top-left',
          }),
          new Expand({
            content: new BasemapGallery({
              view,
            }),
            view,
            expandIconClass: 'esri-icon-basemap',
            group: 'top-left',
          }),
        ],
        'top-left',
      );

      //  Se inicializa y se carga a la vista del mapa el widget de tipo buscador
      const zoom = new Zoom({
        view,
      });
      view.ui.add(zoom, {
        position: 'top-right',
      });

      // Se inicializa y se carga a la vista del mapa el widget de localizador
      const locateBtn = new Locate({
        view,
      });

      view.ui.add(locateBtn, {
        position: 'top-right',
        index: 0,
      });

      const customSearchSource = createCustomSearch({ SearchSource, geometryEngine, Graphic });

      // Se inicializa y se carga a la vista del mapa el widget de tipo buscador
      const searchWidget = new Search({
        view,
        sources: [customSearchSource],
        includeDefaultSources: false,
        locationEnabled: false,
      });

      searchWidget.on('select-result', () => {
        const layerListItemsLength = layerList.operationalItems?.items?.length || 0;
        if (layerListItemsLength) {
          for (let i = 0; i < layerListItemsLength; i++) {
            layerList.operationalItems.items[i].visible = false;
          }
        }
      });

      view.ui.add(searchWidget, {
        position: 'top-left',
        index: 0,
      });

      return () => {
        if (view) {
          //Se destruye la vista del mapa
          view.destroy();
        }
      };
    }
    return () => {};
  }, [esriPackages]);

  useEffect(() => {
    getEsriInitialData();
  }, []);

  return (
    <div className='WebMapView'>
      {isLoading && (<Loader />)}

      <div className='webmap' ref={mapRef} />
    </div>
  );
};

export default WebMapView;
